"use client";

import React from "react";
import { Phone, Mail, ArrowRight, MapPin, ShieldCheck, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export default function QuoteCTA() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Basingstoke",
    to: "/AreasWeCoverPage"
  }, {
    name: "Fleet",
    to: "/AreasWeCoverPage"
  }];
  return <section id="finalquotecta" data-nav="light" className="relative w-full bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl bg-card text-card-foreground border border-border shadow-lg p-8 sm:p-10 lg:p-12">
          {/* Subtle decorative background pattern */}
          <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-64 h-64 rounded-full bg-muted/40 blur-2xl pointer-events-none" />

          <div className="relative z-10">
            {/* Trust and status badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted text-muted-foreground text-xs font-medium mb-6">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>NASC &amp; CHAS accredited</span>
            </div>

            {/* Main heading and description */}
            <div className="max-w-2xl mb-8">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
                Request a scaffolding quote
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Speak directly with our contracts and estimating team to discuss your project requirements, arrange a site survey, or receive a formal written proposal.
              </p>
            </div>

            {/* Action buttons & direct contact details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {/* Primary quote button */}
              <div className="sm:col-span-2 lg:col-span-1 flex items-stretch">
                <Button asChild size="lg" className="w-full h-auto py-4 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-medium rounded-lg shadow-sm flex items-center justify-between transition-[background-color,box-shadow]">
                  <Link to="/ContactRequestQuotePage" className="group">
                    <span>Submit quote request</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </Button>
              </div>

              {/* Phone contact card */}
              <a href="tel:01344929900" className="group flex items-center gap-3.5 p-4 rounded-lg border border-border bg-background/50 hover:bg-accent hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-[background-color,border-color] text-card-foreground">
                <div className="p-2.5 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                  <Phone className="size-5" />
                </div>
                <div>
                  <div className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">Call our team</div>
                  <div className="text-base font-semibold text-foreground tracking-tight">01344 929900</div>
                </div>
              </a>

              {/* Email contact card */}
              <a href="mailto:quotes@bmfscaffolding.co.uk" className="group flex items-center gap-3.5 p-4 rounded-lg border border-border bg-background/50 hover:bg-accent hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-[background-color,border-color] text-card-foreground">
                <div className="p-2.5 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                  <Mail className="size-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">Email specifications</div>
                  <div className="text-sm font-semibold text-foreground truncate">quotes@bmfscaffolding.co.uk</div>
                </div>
              </a>
            </div>

            {/* Quick response note */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
              <Clock className="size-3.5 text-primary shrink-0" />
              <span>Quotes typically issued within 24 to 48 hours following site assessment or drawing review.</span>
            </div>

            <Separator className="my-6" />

            {/* Nearby areas coverage */}
            <div className="flex flex-wrap items-center gap-y-2 gap-x-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5 font-medium text-foreground">
                <MapPin className="size-4 text-primary shrink-0" />
                <span>Nearby areas:</span>
              </div>
              <div className="flex items-center gap-2">
                {nearbyAreas.map((area, index) => <React.Fragment key={area.name}>
                    <Link to={area.to} data-index={index} className="text-foreground hover:text-primary underline underline-offset-4 decoration-border hover:decoration-primary transition-colors font-medium">
                      {area.name}
                    </Link>
                    {index < nearbyAreas.length - 1 && <span className="text-border select-none">•</span>}
                  </React.Fragment>)}
              </div>
              <span className="text-xs text-muted-foreground sm:ml-auto">
                Serving Berkshire, Surrey, Hampshire &amp; the Home Counties
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}